<template>
  <div :class="bem.b()">
    <cms-virtual-list :items="flattenTree" :remain="8" :size="33">
      <template #default="{ node }">
        <cms-tree-node
          :key="node.key"
          :node="node"
          :expanded="isExpand(node)"
          :loading-keys="loadingKeysRef"
          :selected-keys="selectedKeysRef"
          :show-checkbox="showCheckbox"
          :checked="isChecked(node)"
          :disabled="isDisabled(node)"
          :indeterminte="isIndeterminte(node)"
          @toggle="toggleExpand"
          @select="handleSelect"
          @check="toggleCheck"
        />
      </template>
    </cms-virtual-list>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref, useSlots, watch } from 'vue'
import {
  treeEmitts,
  treeInjectKey,
  TreeNode,
  TreeOption,
  treeProps,
  typeKey
} from './tree'
import cmsTreeNode from './tree-node.vue'
import cmsVirtualList from '@chenms-zj/components/virtual-list'
import { createNamespace } from '@chenms-zj/utils/create'

const bem = createNamespace('tree')
defineOptions({
  name: 'cms-tree'
})

const props = defineProps(treeProps)

const tree = ref<TreeNode[]>([])

// 1.用来获取对应的字段
function createOptions(key: string, label: string, children: string) {
  return {
    getKey(node: TreeOption) {
      return node[key] as string
    },
    getLabel(node: TreeOption) {
      return node[label] as string
    },
    getChildren(node: TreeOption) {
      return node[children] as TreeOption[]
    }
  }
}

const treeOptions = createOptions(
  props.keyField,
  props.labelField,
  props.childrenField
)

// 2.将用户传递的数据进行格式化操作
const createTree = (data: TreeOption[], parent: TreeNode | null = null) => {
  function traversal(data: TreeOption[], parent: TreeNode | null = null) {
    return data.map(node => {
      const children = treeOptions.getChildren(node) || []

      const treeNode: TreeNode = {
        key: treeOptions.getKey(node),
        label: treeOptions.getLabel(node),
        children: [], // 默认为空
        rawNode: node,
        level: parent ? parent.level + 1 : 0,
        disabled: !!node.disabled,
        // 判断节点是否自带isLeaf 如果自带了就以自带的为准否则看下有没有children属性
        isLeaf: node.isLeaf ?? children.length === 0,
        parentKey: parent?.key
      }
      if (children.length > 0) {
        // 有下一层再去递归，将其格式化成treeNode类型
        treeNode.children = traversal(children, treeNode)
      }

      return treeNode
    })
  }
  const result: TreeNode[] = traversal(data, parent)
  return result
}

// 监听数据变化，调用格式化方法，一上来就先格式化
watch(
  () => props.data,
  (data: TreeOption[]) => {
    tree.value = createTree(data)
    console.log('tree', tree.value)
  },
  {
    immediate: true
  }
)

// 需要展开的key有哪些
const expandKeysSet = ref(new Set(props.defaultExpandedKeys))

// 3.将树拍平，这里需要依赖当前展开的节点进行动态计算
const flattenTree = computed(() => {
  let expandKeys = expandKeysSet.value // 要展开的key有哪些
  let flattenNodes: TreeNode[] = [] // 最终拍平后的结果
  const nodes = tree.value || [] // 被格式化后的节点
  const stack: TreeNode[] = [] // 用于遍历的栈

  for (let i = nodes.length - 1; i >= 0; --i) {
    stack.push(nodes[i])
  }

  // 深度遍历
  while (stack.length) {
    const node = stack.pop()
    if (!node) continue
    flattenNodes.push(node)
    if (expandKeys.has(node.key)) {
      const children = node.children
      if (children) {
        for (let i = node.children.length - 1; i >= 0; --i) {
          stack.push(node.children[i])
        }
      }
    }
  }

  return flattenNodes
})

const isExpand = (node: TreeNode): boolean => {
  return expandKeysSet.value.has(node.key)
}

const loadingKeysRef = ref(new Set<typeKey>())

// 加载逻辑
const triggerLoading = (node: TreeNode) => {
  // 这个节点需要异步加载
  if (!node.children.length && !node.isLeaf) {
    const loadingKeys = loadingKeysRef.value
    // 如果没有加载过这个节点就加载
    if (!loadingKeys.has(node.key)) {
      loadingKeys.add(node.key)
      const onLoad = props.onLoad
      if (onLoad) {
        onLoad(node.rawNode).then(children => {
          // 修改原来的节点
          node.rawNode.children = children
          // 更新自定义的node
          node.children = createTree(children, node)
          loadingKeys.delete(node.key)
        })
      }
    }
  }
}

// 折叠功能
const collpase = (node: TreeNode) => {
  expandKeysSet.value.delete(node.key)
}
// 展开功能
const expand = (node: TreeNode) => {
  expandKeysSet.value.add(node.key)
  // 动态加载逻辑
  triggerLoading(node)
}
// 4.让用户点击展开
const toggleExpand = (node: TreeNode) => {
  const expandKeys = expandKeysSet.value
  // 如果当前节点正在加载中就不能收起
  if (expandKeys.has(node.key) && !loadingKeysRef.value.has(node.key)) {
    collpase(node)
  } else {
    expand(node)
  }
}

// 5.实现选中节点
const emit = defineEmits(treeEmitts)
const selectedKeysRef = ref<typeKey[]>([])

watch(
  () => props.selectedKeys,
  value => {
    if (value) {
      selectedKeysRef.value = value
    }
  },
  {
    immediate: true
  }
)

// 处理选中的节点
function handleSelect(node: TreeNode) {
  let keys = Array.from(selectedKeysRef.value)
  // 如果不能选择就什么都不做
  if (!props.selectable) {
    return
  }

  if (props.multiple) {
    const index = keys.findIndex(key => key === node.key)
    if (index > -1) {
      keys.splice(index, 1)
    } else {
      keys.push(node.key)
    }
  } else {
    if (keys.includes(node.key)) {
      keys = []
    } else {
      keys = [node.key]
    }
  }
  emit('update:selectedKeys', keys)
}

provide(treeInjectKey, {
  slots: useSlots()
})

const checkedkeysRefs = ref(new Set(props.defaultCheckedKeys))

function isChecked(node: TreeNode) {
  return checkedkeysRefs.value.has(node.key)
}

function isDisabled(node: TreeNode) {
  return !!node.disabled
}

const indeterminteRefs = ref<Set<typeKey>>(new Set())

function isIndeterminte(node: TreeNode) {
  return indeterminteRefs.value.has(node.key)
}

// 自上而下的选中
function toggle(node: TreeNode, checked: boolean) {
  if (!node) {
    return
  }
  const checkedKeys = checkedkeysRefs.value
  // 如果是选中状态就要把半选的去掉
  if (checked) {
    indeterminteRefs.value.delete(node.key)
  }
  checkedKeys[checked ? 'add' : 'delete'](node.key)
  const children = node.children
  if (children) {
    children.forEach(childrenNode => {
      if (!childrenNode.disabled) {
        toggle(childrenNode, checked)
      }
    })
  }
}

function findNode(key: typeKey) {
  return flattenTree.value.find(node => node.key === key)
}

function updateCheckedKeys(node: TreeNode) {
  if(!node) {
    return
  }
  //  自下而上更新
  if (node.parentKey) {
    const parentNode = findNode(node.parentKey)

    if(parentNode) {
      let allChecked = true // 默认儿子应该全选
      let hasChecked = false // 儿子有没有被选中
      const nodes = parentNode.children
      for(const node of nodes) {
        if(checkedkeysRefs.value.has(node.key)) {
          hasChecked = true // 子节点被选中了
        } else if(indeterminteRefs.value.has(node.key)) {
          allChecked = false
          hasChecked = true
        } else {
          allChecked = false
        }
      }
      if(allChecked) {
        checkedkeysRefs.value.add(parentNode.key)
        indeterminteRefs.value.delete(parentNode.key)
      } else if(hasChecked) {
        checkedkeysRefs.value.delete(parentNode.key)
        indeterminteRefs.value.add(parentNode.key)
      }
      updateCheckedKeys(parentNode)
    }
  }


}

function toggleCheck(node: TreeNode, checked: boolean) {
  toggle(node, checked)
  updateCheckedKeys(node)
}

onMounted(() => {
  checkedkeysRefs.value.forEach((key: typeKey) => {
    toggleCheck(findNode(key)!, true)
  })
})
</script>

<style scoped></style>
